import { useMemo } from 'react';
import { AlertTriangle, ArrowRight, GitBranch, Layers3, ListChecks, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { usePortfolioProjects } from '@/hooks/usePortfolioProjects';
import { useProgramData } from '@/hooks/useRoleHomeData';
import { humanizeRule } from '@/components/risk/riskUtils';
import { healthTone, summarisePortfolio, worstFirst, type HealthTone } from '@/lib/pmoSummary';
import { riskBreakdown } from '@/lib/homeSummaries';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from './HomeFrame';

interface Props { onNavigate: (view: string, params?: Record<string, string>) => void; }

const TONE_CLASS: Record<HealthTone, string> = { 'on-track': 'bg-success/10 text-foreground', 'at-risk': 'bg-warning/15 text-foreground', critical: 'bg-destructive/10 text-destructive' };
const TONE_LABEL: Record<HealthTone, string> = { 'on-track': 'On track', 'at-risk': 'At risk', critical: 'Critical' };
const SEVERITY_RANK: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 };

/** Program manager home: health across projects, the risks behind it, blocked dependencies and actions to review. */
const ProgramBoard = ({ onNavigate }: Props) => {
  const { t, tx, ago, shortDate } = useLocalization();
  const projectsQuery = usePortfolioProjects();
  const d = useProgramData();

  const allProjects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);
  const portfolio = useMemo(() => summarisePortfolio(allProjects), [allProjects]);
  const projects = useMemo(() => worstFirst(allProjects).slice(0, 8), [allProjects]);
  const risk = useMemo(() => riskBreakdown(d.risks), [d.risks]);
  const topRisks = useMemo(() => [...d.risks].sort((a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0)).slice(0, 6), [d.risks]);

  const { projectIdFor, nameFor, riskCountByProject } = useMemo(() => {
    const byKey = new Map<string, { id: string; name: string }>();
    allProjects.forEach((p) => { byKey.set(p.id, p); if (p.external_id) byKey.set(p.external_id, p); });
    const taskByKey = new Map<string, { title: string; project: string | null }>();
    d.tasks.forEach((task) => { if (task.external_id) taskByKey.set(task.external_id, { title: task.title, project: task.project_external_id }); });
    const pid = (type: string, id: string) => (type === 'project' ? byKey.get(id)?.id : byKey.get(taskByKey.get(id)?.project ?? '')?.id);
    const counts = new Map<string, number>();
    d.risks.forEach((r) => { const p = pid(r.entity_type, r.entity_id); if (p) counts.set(p, (counts.get(p) ?? 0) + 1); });
    return {
      projectIdFor: pid,
      nameFor: (type: string, id: string) => (type === 'project' ? byKey.get(id)?.name : taskByKey.get(id)?.title),
      riskCountByProject: counts,
    };
  }, [allProjects, d.tasks, d.risks]);

  if (d.isLoading || projectsQuery.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('dependencies')}>{t('Open dependencies')}</Button>
        <Button onClick={() => onNavigate('risks')}>{t('Review risks')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile label={t('Projects')} icon={<Layers3 className="h-4 w-4" />} value={portfolio.total}
          tone={portfolio.critical > 0 ? 'critical' : portfolio.atRisk > 0 ? 'warning' : 'default'}
          hint={portfolio.total ? t('{0} at risk, {1} critical', { 0: portfolio.atRisk, 1: portfolio.critical }) : t('No projects synced yet.')}
          onClick={() => onNavigate('projects')} />
        <StatTile label={t('Open risks')} icon={<ShieldAlert className="h-4 w-4" />} value={risk.open}
          tone={risk.highOrCritical > 0 ? 'critical' : 'default'} hint={t('{0} high or critical', { 0: risk.highOrCritical })}
          onClick={() => onNavigate('risks')} />
        <StatTile label={t('Blocked dependencies')} icon={<GitBranch className="h-4 w-4" />} value={risk.dependencies.length}
          tone={risk.dependencies.length > 0 ? 'warning' : 'default'} hint={risk.dependencies.length ? t('work waiting on other work') : t('Nothing is waiting on a dependency.')}
          onClick={() => onNavigate('dependencies')} />
        <StatTile label={t('Actions to review')} icon={<ListChecks className="h-4 w-4" />} value={d.pendingActions.length}
          tone={d.pendingActions.length > 0 ? 'warning' : 'default'} hint={t('extracted from meetings and email')}
          onClick={() => onNavigate('actionsRequired')} />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Projects')} hint={t('Worst first, with open risks. Open a project to see its risks.')}
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate('portfolio')}>{t('View all')} <ArrowRight className="ms-1 h-4 w-4 rtl:rotate-180" /></Button>}>
          {projects.length === 0 ? (
            <HomeEmpty>{projectsQuery.isError ? t('Projects could not be loaded.') : t('No projects synced yet.')}</HomeEmpty>
          ) : projects.map((p) => {
            const tone = healthTone(p.health);
            const n = riskCountByProject.get(p.id) ?? 0;
            return (
              <HomeRow key={p.id} onClick={() => onNavigate('risks', { project: p.id })} label={t('{0}: {1}. Open its risks.', { 0: p.name, 1: t(TONE_LABEL[tone]) })}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{p.name}</span>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', TONE_CLASS[tone])}>{t(TONE_LABEL[tone])}</span>
                    {n > 0 && <span className="shrink-0 text-[11px] text-muted-foreground">{t(n === 1 ? '{0} open risk' : '{0} open risks', { 0: n })}</span>}
                  </span>
                  <Progress value={p.progress ?? 0} className="mt-2 h-1.5" />
                </span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{p.progress ?? 0}%</span>
              </HomeRow>
            );
          })}
        </HomePanel>

        <HomePanel title={t('Top risks')} hint={t('Highest severity first.')} action={<Badge variant="outline">{risk.open}</Badge>}>
          {topRisks.length === 0 ? (
            <HomeEmpty>{d.risksFailed ? t('Risks could not be loaded.') : t('No open risks.')}</HomeEmpty>
          ) : topRisks.map((r) => {
            const pid = projectIdFor(r.entity_type, r.entity_id);
            return (
              <HomeRow key={r.id} onClick={() => onNavigate('risks', pid ? { project: pid } : undefined)}>
                <AlertTriangle className={cn('h-4 w-4 shrink-0', r.severity === 'critical' || r.severity === 'high' ? 'text-destructive' : 'text-warning')} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{nameFor(r.entity_type, r.entity_id) ?? tx(humanizeRule(r.rule_id))}</span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">{tx(humanizeRule(r.rule_id))} · {tx(r.severity)} · {ago(r.detected_at)}</span>
                </span>
              </HomeRow>
            );
          })}
        </HomePanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Actions to review')} hint={t('Suggested by AI from meetings and email. Nothing is tracked until a person approves it.')} action={<Badge variant="outline">{d.pendingActions.length}</Badge>}>
          {d.pendingActions.length === 0 ? (
            <HomeEmpty>{d.pendingFailed ? t('Action items could not be loaded.') : t('No action items are waiting for review.')}</HomeEmpty>
          ) : d.pendingActions.slice(0, 6).map((a) => (
            <HomeRow key={a.id} onClick={() => onNavigate('actionsRequired')}>
              <ListChecks className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{a.title}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {[a.assignee_name ?? t('Unassigned'), a.due_date ? t('due {0}', { 0: shortDate(a.due_date) }) : null].filter(Boolean).join(' · ')}
                </span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>

        <HomePanel title={t('Blocked dependencies')} hint={t('Tasks that cannot move until another task does.')} action={<Badge variant="outline">{risk.dependencies.length}</Badge>}>
          {risk.dependencies.length === 0 ? (
            <HomeEmpty>{t('Nothing is waiting on a dependency.')}</HomeEmpty>
          ) : risk.dependencies.slice(0, 5).map((r) => {
            const pid = projectIdFor(r.entity_type, r.entity_id);
            return (
              <HomeRow key={r.id} onClick={() => onNavigate('risks', pid ? { project: pid } : undefined)}>
                <GitBranch className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{nameFor(r.entity_type, r.entity_id) ?? t('Unknown')}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{tx(r.severity)} · {ago(r.detected_at)}</span>
                </span>
              </HomeRow>
            );
          })}
        </HomePanel>
      </div>
    </div>
  );
};

export default ProgramBoard;
